// Pure helpers for tour session mode / bucket navigation decisions.

import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { BucketId } from "@/components/shell/modes/bucketsRegistry";
import type { FireflyTourStep } from "./tourTypes";

/** Auto-navigate to step.target.mode only while the workspace is still on the prior bucket. */
export function shouldAutoNavigateAfterBucketPick(input: {
  afterBucketPick: boolean;
  deferModeNavigation: boolean;
  targetMode: PrimaryMode;
  currentMode: PrimaryMode;
  activeBucket: BucketId | null;
  targetBucket: BucketId | null;
}): boolean {
  if (!input.afterBucketPick || !input.deferModeNavigation) return false;
  if (input.targetMode === input.currentMode) return false;
  if (!input.targetBucket || !input.activeBucket) return false;
  return input.activeBucket !== input.targetBucket;
}

export function tourStepSettleMs(prevStepId: string | undefined): number {
  if (prevStepId?.startsWith("prelude-bucket-pick-")) return 320;
  return 80;
}

export function shouldDelaySkipWhenAlreadyOnMode(prevStepId: string | undefined): boolean {
  return prevStepId?.startsWith("prelude-bucket-pick-") ?? false;
}

/**
 * A step that is already satisfied (current mode matches skipWhenCurrentMode)
 * must advance with the event that step accepts. Wait-for-click steps ignore "next".
 */
export function tourAdvanceEventForSatisfiedStep(
  waitForUserAction: boolean,
): "user_action" | "next" {
  return waitForUserAction ? "user_action" : "next";
}

/** Drop a leading run of steps that are already satisfied, so the session opens on a visible step. */
export function dropLeadingModeSkips(
  steps: readonly FireflyTourStep[],
  currentMode: PrimaryMode,
): FireflyTourStep[] {
  if (steps.length <= 1) return [...steps];
  let index = 0;
  while (index < steps.length - 1 && steps[index].skipWhenCurrentMode === currentMode) {
    index += 1;
  }
  return steps.slice(index);
}
